import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Tabs,
    Tab,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Sync as SyncIcon,
    PersonAdd as PersonAddIcon,
    ExpandMore as ExpandMoreIcon,
    Info as InfoIcon,
} from '@mui/icons-material';
import hostService from '../services/hostService';
import memberService from '../services/memberService';
import MemberTable from './MemberTable';
import InviteMemberModal from './InviteMemberModal';
import { toast } from 'react-toastify';
import HomeLayout from '../../../components/layout/HomeLayout';
import LockedModal from '../../subscription/components/LockedModal';
import subscriptionService from '../../../services/subscriptionService';

const TabPanel = ({ children, value, index, ...other }) => {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

const MaxMemberCard = ({ host, onUpdate }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(host.maxMember || 10);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            await hostService.updateHost(host.id, { maxMember: value });
            toast.success('Cập nhật giới hạn thành viên thành công');
            setEditing(false);
            onUpdate();
        } catch (error) {
            toast.error('Cập nhật thất bại');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card variant="outlined">
            <CardContent>
                <Typography color="text.secondary" gutterBottom>Giới hạn thành viên (Max Member)</Typography>
                {editing ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            size="small"
                            type="number"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            disabled={saving}
                            inputProps={{ min: 1 }}
                            sx={{ width: 100 }}
                        />
                        <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
                            Lưu
                        </Button>
                        <Button size="small" onClick={() => setEditing(false)} disabled={saving}>
                            Hủy
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6">{host.maxMember || 5}</Typography>
                        <Button size="small" onClick={() => { setValue(host.maxMember || 5); setEditing(true); }}>
                            Chỉnh sửa
                        </Button>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

const HostDetailPage = () => {
    const { hostId } = useParams();
    const navigate = useNavigate();
    const [host, setHost] = useState(null);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [inviteModalOpen, setInviteModalOpen] = useState(false);

    // Subscription Check
    const [isLocked, setIsLocked] = useState(false);
    const [checkingSub, setCheckingSub] = useState(true);
    const [lockReason, setLockReason] = useState(null);

    useEffect(() => {
        checkAccess();
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostId]);

    const checkAccess = async () => {
        try {
            setCheckingSub(true);
            const subRes = await subscriptionService.getCurrentSubscription();
            if (!subRes.success || !subRes.data || subRes.data.isExpired) {
                setIsLocked(true);
                if (subRes.data?.isExpired) {
                    setLockReason(`Gói cước của bạn đã hết hạn vào ngày ${new Date(subRes.data.endDate).toLocaleDateString('vi-VN')}. Vui lòng gia hạn để tiếp tục sử dụng.`);
                } else if (subRes.data?.status === 'CANCELLED') {
                    setLockReason('Gói cước của bạn đã bị hủy. Vui lòng đăng ký gói mới.');
                } else {
                    setLockReason('Bạn cần đăng ký gói cước để sử dụng tính năng này.');
                }
            }
        } catch (e) {
            console.error('Check access error:', e);
            setIsLocked(true);
            setLockReason('Không thể kiểm tra trạng thái gói cước.');
        } finally {
            setCheckingSub(false);
        }
    };

    const fetchData = async () => {
        try {
            const [hostData, memberData] = await Promise.all([
                hostService.getHostById(hostId),
                memberService.getMembers(hostId)
            ]);
            setHost(hostData);
            setMembers(Array.isArray(memberData) ? memberData : (memberData.members || []));
        } catch (error) {
            if (error.response?.status === 403 && error.response?.data?.code === 'SUBSCRIPTION_REQUIRED') {
                setIsLocked(true);
                setLockReason(error.response.data.message);
            } else {
                console.error(error);
                if (!isLocked) {
                    // Check access might have handled it, else potentially redirect or show error
                    // navigate('/hosts'); // Removing redirect to avoid loop/bad UX if just one fail
                    toast.error('Không thể tải thông tin tài khoản');
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        try {
            await memberService.syncMembers(hostId);
            toast.success('Đồng bộ thành viên thành công');
            fetchData(); // Reload data
        } catch (error) {
            toast.error('Đồng bộ thất bại');
        } finally {
            setSyncing(false);
        }
    };

    if (loading || checkingSub) {
        return (
            <HomeLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                    <CircularProgress />
                </Box>
            </HomeLayout>
        );
    }

    if (isLocked) {
        return (
            <HomeLayout>
                <Box sx={{ p: 3 }}>
                    <LockedModal alertMessage={lockReason} />
                </Box>
            </HomeLayout>
        );
    }

    if (!host) return null;

    return (
        <HomeLayout>
            <Box sx={{ p: 3, minHeight: '80vh' }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/hosts')}
                    sx={{ mb: 2 }}
                >
                    Quay lại danh sách
                </Button>

                <Accordion
                    elevation={0}
                    sx={{
                        mb: 3,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '12px !important',
                        overflow: 'hidden',
                        '&:before': { display: 'none' }
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InfoIcon color="info" />
                            <Typography fontWeight="medium">Hướng dẫn Quản lý Thành viên & Đồng bộ</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }} color="primary">1. Quy trình Mời & Xác nhận</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • <b>Mời mới</b>: Sau khi mời, thành viên sẽ ở trạng thái <i>Pending</i> (chưa có ID OpenAI).<br />
                                    • <b>Xác nhận</b>: Người dùng cần check email và chấp nhận lời mời từ OpenAI.<br />
                                    • <b>Đồng bộ</b>: Sau khi họ xác nhận, hãy nhấn <b>Đồng bộ dữ liệu</b> để hệ thống lấy <code>OpenAI User ID</code>. ID này là bắt buộc để thực hiện các thao tác quản lý sau này.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }} color="primary">2. Lưu ý khi Xóa/Sửa</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • <b>Ưu tiên Database</b>: Hệ thống luôn xóa trong Database trước để bạn chủ động quản lý giao diện.<br />
                                    • <b>Khớp dữ liệu</b>: Nếu dữ liệu trên GPT và Database lệch nhau, hãy dùng nút <b>Đồng bộ</b> để cập nhật lại trạng thái mới nhất từ OpenAI.<br />
                                    • <b>Giới hạn</b>: Kiểm tra "Max Member" ở tab Tổng quan để biết số lượng thành viên tối đa có thể mời.
                                </Typography>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>

                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' }, 
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', md: 'center' }, 
                    mb: 3,
                    gap: 2
                }}>
                    <Typography variant="h4" sx={{ 
                        wordBreak: 'break-all',
                        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } 
                    }}>
                        {host.openaiEmail}
                    </Typography>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' }, 
                        gap: 1.5,
                        width: { xs: '100%', md: 'auto' }
                    }}>
                        <Button
                            variant="outlined"
                            startIcon={<SyncIcon />}
                            onClick={handleSync}
                            disabled={syncing}
                            fullWidth
                            size="small"
                            sx={{ py: 0.5, fontSize: '0.8rem' }}
                        >
                            {syncing ? 'Đang đồng bộ...' : 'Đồng bộ dữ liệu'}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<PersonAddIcon />}
                            onClick={() => setInviteModalOpen(true)}
                            fullWidth
                            size="small"
                            sx={{ py: 0.5, fontSize: '0.8rem' }}
                        >
                            Mời thành viên
                        </Button>
                    </Box>
                </Box>

                <Paper sx={{ width: '100%' }}>
                    <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)} sx={{ px: 2, pt: 1 }}>
                        <Tab label="Tổng quan" />
                        <Tab label={`Thành viên (${members.length})`} />
                    </Tabs>

                    <Divider />

                    <Box sx={{ p: 2 }}>
                        <TabPanel value={tabValue} index={0}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>Trạng thái tài khoản</Typography>
                                            <Typography variant="h6">{host.status}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>Đồng bộ lần cuối</Typography>
                                            <Typography variant="h6">
                                                {host.lastSyncAt ? new Date(host.lastSyncAt).toLocaleString('vi-VN') : 'Chưa đồng bộ'}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <MaxMemberCard host={host} onUpdate={fetchData} />
                                </Grid>
                            </Grid>
                        </TabPanel>

                        <TabPanel value={tabValue} index={1}>
                            <MemberTable
                                members={members}
                                hostId={hostId}
                                onRefresh={fetchData}
                            />
                        </TabPanel>
                    </Box>
                </Paper>

                <InviteMemberModal
                    open={inviteModalOpen}
                    onClose={() => setInviteModalOpen(false)}
                    hostAccountId={hostId}
                    onInvited={fetchData}
                />
            </Box >
        </HomeLayout >
    );
};

export default HostDetailPage;
